import { AnimatePresence, motion } from "framer-motion";
import { useEffect, useMemo, useState } from "react";
import { Link, useParams, Navigate } from "react-router-dom";
import { CATEGORIES } from "@/data/productCategories";
import Breadcrumbs from "@/components/seo/Breadcrumbs";
import { productSchema } from "@/lib/seo/schemas";
import { safeJsonLd } from "@/lib/seo/safeJson";

const FILTERS = [
  { key: "all", label: "All" },
  { key: "door", label: "Doors" },
  { key: "window", label: "Windows" },
  { key: "screen", label: "Screens" },
];

export default function ProductCategory() {
  const { slug } = useParams();
  const cat = CATEGORIES[slug];
  // eslint-disable-next-line no-console
  console.log('[PC-DEBUG]', 'slug=', slug, 'hasCat=', !!cat, 'hasDesigns=', !!CATEGORIES.designs);
  const [activeFilter, setActiveFilter] = useState("all");
  const [lightbox, setLightbox] = useState(null);

  useEffect(() => {
    if (!lightbox) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape") setLightbox(null);
    };
    window.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [lightbox]);

  const cleanProducts = useMemo(() => {
    if (!cat || cat.layoutStyle !== "clean") return [];
    const prefix = cat.codePrefix || "ESP";
    const gallerySize = cat.gallery?.length || 0;
    return [...(cat.gallery || []), ...(cat.lifestyleGallery || [])].map(
      (item, i) => ({
        ...item,
        code: `${prefix}${i + 1}`,
        isStudio: item.isStudio !== undefined ? item.isStudio : i < gallerySize,
      })
    );
  }, [cat]);

  if (!cat) return <Navigate to="/products" replace />;

  const crumbs = [
    { name: "Home", path: "/" },
    { name: "Products", path: "/products" },
    { name: cat.title.replace(/\.$/, ""), path: `/products/${cat.slug}` },
  ];
  const productLd = productSchema({
    name: cat.title.replace(/\.$/, ""),
    description: cat.seo?.description || cat.description,
    image: cat.heroImage,
    sku: (cat.codePrefix || cat.slug).toUpperCase(),
    slug: cat.slug,
    category: cat.eyebrow || "Steel Joinery",
  });
  const relatedCats = (cat.relatedSlugs || [])
    .map((s) => CATEGORIES[s])
    .filter(Boolean)
    .slice(0, 5);

  // Clean white layout — used for the Designs page (matches euro-steel.co.nz/products)
  if (cat.layoutStyle === "clean") {
    const filtered =
      activeFilter === "all"
        ? cleanProducts
        : cleanProducts.filter((p) => p.type === activeFilter);
    const counts = cleanProducts.reduce((acc, p) => {
      acc[p.type] = (acc[p.type] || 0) + 1;
      return acc;
    }, {});
    return (
      <div
        data-testid={`product-category-${slug}`}
        className="min-h-screen bg-white text-[#0a0a0c]"
      >
        {/* Product JSON-LD */}
        <script
          type="application/ld+json"
          // eslint-disable-next-line react/no-danger
          dangerouslySetInnerHTML={{ __html: safeJsonLd(productLd) }}
        />
        {/* Minimal white header */}
        <section className="mx-auto max-w-[1400px] px-6 pb-10 pt-40 text-center lg:px-16 lg:pt-48">
          <div className="mb-6">
            <Breadcrumbs items={crumbs} tone="light" />
          </div>
          {cat.eyebrow ? (
            <motion.span
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, ease: [0.19, 1, 0.22, 1] }}
              data-testid="product-category-eyebrow"
              className="font-tech text-[10px] uppercase tracking-[0.5em] text-[#8a6a3d]"
            >
              — {cat.eyebrow}
            </motion.span>
          ) : null}
          <motion.h1
            initial={{ opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 0.08, ease: [0.19, 1, 0.22, 1] }}
            data-testid="product-category-heading"
            className="mt-5 font-serif-display text-5xl font-bold uppercase leading-[0.98] tracking-tight text-[#0a0a0c] lg:text-7xl"
          >
            {cat.title}
          </motion.h1>
          {cat.tagline ? (
            <motion.p
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.14, ease: [0.19, 1, 0.22, 1] }}
              className="mt-5 font-tech text-[11px] uppercase tracking-[0.4em] text-[#c6a87c]"
            >
              {cat.tagline}
            </motion.p>
          ) : null}
          <motion.p
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2, ease: [0.19, 1, 0.22, 1] }}
            className="mx-auto mt-6 max-w-2xl font-body text-[14px] leading-[1.75] text-[#0a0a0c]/70 lg:text-[15px]"
          >
            {cat.description}
          </motion.p>
        </section>

        {!cat.hideFilters && (
        <section className="mx-auto max-w-[1400px] px-6 pb-10 lg:px-16">
          <div
            data-testid="designs-filter-bar"
            className="flex flex-wrap items-center justify-center gap-3 border-y border-[#0a0a0c]/10 py-5"
          >
            {FILTERS.map((f) => {
              const isActive = activeFilter === f.key;
              const count =
                f.key === "all" ? cleanProducts.length : counts[f.key] || 0;
              return (
                <button
                  key={f.key}
                  type="button"
                  data-testid={`filter-${f.key}`}
                  onClick={() => setActiveFilter(f.key)}
                  className={`group inline-flex items-center gap-2 rounded-full border px-6 py-2.5 font-tech text-[10px] uppercase tracking-[0.32em] transition-all duration-300 ${
                    isActive
                      ? "border-[#0a0a0c] bg-[#0a0a0c] text-white"
                      : "border-[#0a0a0c]/20 bg-transparent text-[#0a0a0c]/70 hover:border-[#c6a87c] hover:text-[#c6a87c]"
                  }`}
                >
                  <span>{f.label}</span>
                  <span
                    className={`font-tech text-[9px] tracking-normal ${
                      isActive ? "text-white/60" : "text-[#0a0a0c]/40"
                    }`}
                  >
                    {count}
                  </span>
                </button>
              );
            })}
          </div>
        </section>
        )}

        {/* Clean product grid */}
        <section className="mx-auto max-w-[1400px] px-6 pb-24 lg:px-16 lg:pb-32">
          {filtered.length === 0 ? (
            <div
              data-testid="designs-empty-state"
              className="border border-dashed border-[#0a0a0c]/15 py-24 text-center"
            >
              <p className="font-tech text-[11px] uppercase tracking-[0.32em] text-[#0a0a0c]/50">
                No designs in this category yet.
              </p>
              <button
                type="button"
                onClick={() => setActiveFilter("all")}
                className="mt-4 font-body text-[13px] text-[#c6a87c] hover:underline"
              >
                Show all designs
              </button>
            </div>
          ) : (
            <div className="grid grid-cols-2 gap-x-6 gap-y-14 sm:grid-cols-2 lg:grid-cols-3 lg:gap-x-10 lg:gap-y-20">
              {filtered.map((p, i) => (
                <motion.div
                  key={p.code}
                  initial={{ opacity: 0, y: 24 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{
                    duration: 0.6,
                    delay: (i % 3) * 0.05,
                    ease: [0.19, 1, 0.22, 1],
                  }}
                >
                  <button
                    type="button"
                    onClick={() => setLightbox(p)}
                    data-testid={`design-card-${p.code.toLowerCase()}`}
                    className="group block w-full text-left"
                  >
                    <div className="relative aspect-[3/4] w-full overflow-hidden bg-[#f4f2ee]">
                      <img
                        src={p.src}
                        alt={p.code}
                        loading="lazy"
                        className={
                          p.isStudio
                            ? "absolute inset-0 h-full w-full object-contain p-4 transition-transform duration-[900ms] ease-[cubic-bezier(0.19,1,0.22,1)] group-hover:scale-[1.03] lg:p-6"
                            : "absolute inset-0 h-full w-full object-cover transition-transform duration-[900ms] ease-[cubic-bezier(0.19,1,0.22,1)] group-hover:scale-[1.03]"
                        }
                      />
                      <span
                        aria-hidden
                        className="pointer-events-none absolute inset-x-0 bottom-0 flex items-center justify-center bg-gradient-to-t from-black/50 to-transparent py-4 opacity-0 transition-opacity duration-500 group-hover:opacity-100"
                      >
                        <span className="font-tech text-[10px] uppercase tracking-[0.32em] text-white">
                          Enlarge +
                        </span>
                      </span>
                    </div>
                    <figcaption className="mt-4 text-center">
                      <p className="font-tech text-[13px] uppercase tracking-[0.35em] text-[#0a0a0c] transition-colors duration-300 group-hover:text-[#c6a87c]">
                        {p.code}
                      </p>
                    </figcaption>
                  </button>
                </motion.div>
              ))}
            </div>
          )}

          {/* CTA row — white theme */}
          <div className="mt-24 flex flex-wrap justify-center gap-4 border-t border-[#0a0a0c]/10 pt-16">
            <Link
              to="/contact"
              data-testid="product-category-enquire"
              className="rounded-full bg-[#0a0a0c] px-12 py-4 font-tech text-[11px] uppercase tracking-[0.35em] text-white transition-all duration-500 hover:bg-[#c6a87c]"
            >
              Enquire
            </Link>
            <Link
              to="/products"
              className="rounded-full border border-[#0a0a0c]/25 px-12 py-4 font-tech text-[11px] uppercase tracking-[0.35em] text-[#0a0a0c]/80 transition-all duration-500 hover:border-[#c6a87c] hover:text-[#c6a87c]"
            >
              All Products
            </Link>
          </div>
        </section>

        {/* Lightbox — photo-only enlarge on card click */}
        <AnimatePresence>
          {lightbox && (
            <motion.div
              data-testid="design-lightbox"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.3 }}
              onClick={() => setLightbox(null)}
              className="fixed inset-0 z-[120] flex items-center justify-center bg-black/85 p-4 backdrop-blur-sm"
            >
              <button
                type="button"
                data-testid="design-lightbox-close"
                onClick={() => setLightbox(null)}
                aria-label="Close"
                className="absolute right-6 top-6 flex h-10 w-10 items-center justify-center rounded-full border border-white/25 text-white/80 transition hover:border-white hover:text-white"
              >
                <span className="text-lg leading-none">×</span>
              </button>
              <span className="absolute left-6 top-6 font-tech text-[11px] uppercase tracking-[0.35em] text-white/80">
                {lightbox.code}
              </span>
              <motion.img
                key={lightbox.code}
                initial={{ scale: 0.96, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                exit={{ scale: 0.96, opacity: 0 }}
                transition={{ duration: 0.35, ease: [0.19, 1, 0.22, 1] }}
                onClick={(e) => e.stopPropagation()}
                src={lightbox.src}
                alt={lightbox.code}
                className="max-h-[90vh] max-w-[92vw] object-contain"
              />
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    );
  }

  return (
    <div
      data-testid={`product-category-${slug}`}
      className="bg-[#0a0a0c] text-white"
    >
      {/* Product JSON-LD */}
      <script
        type="application/ld+json"
        // eslint-disable-next-line react/no-danger
        dangerouslySetInnerHTML={{ __html: safeJsonLd(productLd) }}
      />
      {/* Page hero — clean, centred, bold */}
      <section className="relative min-h-[80vh] w-full overflow-hidden">
        <img
          src={cat.heroImage}
          alt=""
          aria-hidden
          className="absolute inset-0 h-full w-full object-cover opacity-55"
        />
        {/* Vignette overlays that push a warm bronze tint from the edges. */}
        <div
          aria-hidden
          className="absolute inset-0 bg-gradient-to-b from-black/70 via-black/45 to-black/90"
        />
        <div
          aria-hidden
          className="absolute inset-0 bg-[radial-gradient(circle_at_50%_60%,rgba(198,168,124,0.18),transparent_65%)]"
        />

        <div className="relative mx-auto flex min-h-[80vh] max-w-[1200px] flex-col items-center justify-center px-6 py-24 text-center lg:px-16">
          <div className="mb-6">
            <Breadcrumbs items={crumbs} tone="dark" />
          </div>
          <motion.span
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, ease: [0.19, 1, 0.22, 1] }}
            data-testid="product-category-eyebrow"
            className="font-tech text-[10px] uppercase tracking-[0.5em] text-[#c6a87c]"
          >
            — {cat.eyebrow}
          </motion.span>

          <motion.h1
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 0.1, ease: [0.19, 1, 0.22, 1] }}
            data-testid="product-category-heading"
            className="mt-6 font-serif-display text-6xl font-bold uppercase leading-[0.98] tracking-[-0.015em] text-white drop-shadow-[0_6px_24px_rgba(0,0,0,0.6)] lg:text-8xl"
          >
            {cat.title}
          </motion.h1>

          {/* Bronze underline accent */}
          <motion.span
            aria-hidden
            initial={{ opacity: 0, scaleX: 0 }}
            animate={{ opacity: 1, scaleX: 1 }}
            transition={{ duration: 0.9, delay: 0.35, ease: [0.19, 1, 0.22, 1] }}
            className="mt-8 block h-[2px] w-24 origin-center bg-gradient-to-r from-transparent via-[#c6a87c] to-transparent"
          />

          <motion.p
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.4, ease: [0.19, 1, 0.22, 1] }}
            className="mt-8 font-tech text-[11px] uppercase tracking-[0.4em] text-[#c6a87c]/90"
          >
            {cat.tagline}
          </motion.p>

          <motion.p
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.5, ease: [0.19, 1, 0.22, 1] }}
            className="mt-6 max-w-2xl font-body text-[15px] leading-[1.75] text-white/85 lg:text-[17px]"
          >
            {cat.description}
          </motion.p>
        </div>
      </section>

      {/* Editorial intro — one architectural paragraph, one keyword woven in */}
      {cat.intro && (
        <section
          data-testid="category-intro"
          className="mx-auto max-w-[900px] px-6 pt-24 text-center lg:px-16 lg:pt-32"
        >
          <motion.p
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-100px" }}
            transition={{ duration: 0.9, ease: [0.19, 1, 0.22, 1] }}
            className="font-body text-[17px] leading-[1.85] text-white/75 lg:text-[19px]"
          >
            {cat.intro}
          </motion.p>
        </section>
      )}

      {/* Item list — clean editorial with intense bronze hover */}
      <section className="mx-auto max-w-[1200px] px-6 py-24 lg:px-16 lg:py-32">
        <div className="grid grid-cols-1 gap-x-14 gap-y-4 md:grid-cols-2 lg:grid-cols-3">
          {cat.items.map((it, i) => (
            <motion.div
              key={it.name}
              data-testid={`category-item-${it.name.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{
                duration: 0.7,
                delay: (i % 3) * 0.08,
                ease: [0.19, 1, 0.22, 1],
              }}
              className="group relative cursor-default border-t border-white/15 py-8 transition-colors duration-500 hover:border-[#c6a87c]"
            >
              {/* Bronze wash on hover */}
              <span
                aria-hidden
                className="pointer-events-none absolute inset-0 -z-0 bg-gradient-to-b from-[#c6a87c]/[0.06] to-transparent opacity-0 transition-opacity duration-500 group-hover:opacity-100"
              />
              <div className="relative">
                <span className="font-tech text-[10px] uppercase tracking-[0.32em] text-[#c6a87c]">
                  0{i + 1}
                </span>
                <h3 className="mt-3 font-serif-display text-2xl font-bold uppercase leading-tight tracking-tight text-white transition-colors duration-500 group-hover:text-[#c6a87c] lg:text-[26px]">
                  {it.name}
                </h3>
                <p className="mt-3 font-body text-[14px] leading-[1.7] text-white/70 transition-colors duration-500 group-hover:text-white/90">
                  {it.desc}
                </p>
              </div>
            </motion.div>
          ))}
        </div>

        {/* Product galleries — studio configs and/or lifestyle in-home shots */}
        {[
          cat.gallery && cat.gallery.length > 0
            ? {
                key: "primary",
                items: cat.gallery,
                isLifestyle: cat.galleryStyle === "lifestyle",
                eyebrow: cat.galleryStyle === "lifestyle" ? "Projects On-Site" : "Selected Configurations",
                title: cat.galleryStyle === "lifestyle" ? "Installed Works" : "Studio Reference",
                labelPrefix: cat.galleryStyle === "lifestyle" ? "Project" : "Config",
              }
            : null,
          cat.lifestyleGallery && cat.lifestyleGallery.length > 0
            ? {
                key: "lifestyle",
                items: cat.lifestyleGallery,
                isLifestyle: true,
                eyebrow: "In Situ",
                title: "In-Home Details",
                labelPrefix: "Detail",
              }
            : null,
        ]
          .filter(Boolean)
          .map((block) => (
          <div
            key={block.key}
            data-testid={`category-gallery-${block.key}`}
            className="mt-24 border-t border-white/10 pt-20"
          >
            <div className="mb-14 text-center">
              <span className="font-tech text-[10px] uppercase tracking-[0.5em] text-[#c6a87c]">
                — {block.eyebrow}
              </span>
              <h2 className="mt-4 font-serif-display text-3xl font-bold uppercase tracking-tight text-white lg:text-4xl">
                {block.title}
              </h2>
            </div>
            <div
              className={`grid grid-cols-1 gap-6 sm:grid-cols-2 ${
                block.items.length >= 3 ? "lg:grid-cols-3" : "lg:grid-cols-2"
              }`}
            >
              {block.items.map((g, i) => {
                const isLifestyle = block.isLifestyle;
                return (
                  <motion.figure
                    key={g.src}
                    data-testid={`gallery-item-${i}`}
                    initial={{ opacity: 0, y: 28 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, margin: "-60px" }}
                    transition={{
                      duration: 0.8,
                      delay: (i % 3) * 0.1,
                      ease: [0.19, 1, 0.22, 1],
                    }}
                    className={
                      isLifestyle
                        ? "group relative overflow-hidden rounded-sm border border-white/10 bg-[#0f0f11] transition-all duration-500 hover:border-[#c6a87c] hover:shadow-[0_0_50px_-8px_rgba(198,168,124,0.35)]"
                        : "group relative overflow-hidden rounded-sm border border-white/10 bg-[#f5f2ec] transition-all duration-500 hover:border-[#c6a87c] hover:shadow-[0_0_50px_-8px_rgba(198,168,124,0.35)]"
                    }
                  >
                    <div
                      className={
                        isLifestyle
                          ? "relative aspect-[3/4] overflow-hidden bg-black"
                          : "relative aspect-[3/4] overflow-hidden bg-[#efe9df]"
                      }
                    >
                      <img
                        src={g.src}
                        alt={g.name}
                        loading="lazy"
                        className={
                          isLifestyle
                            ? "absolute inset-0 h-full w-full object-cover transition-transform duration-[900ms] ease-[cubic-bezier(0.19,1,0.22,1)] group-hover:scale-[1.05]"
                            : "absolute inset-0 h-full w-full object-contain p-8 transition-transform duration-[900ms] ease-[cubic-bezier(0.19,1,0.22,1)] group-hover:scale-[1.04]"
                        }
                      />
                      {isLifestyle && (
                        <div
                          aria-hidden
                          className="pointer-events-none absolute inset-0 bg-gradient-to-t from-black/70 via-black/10 to-transparent opacity-80 transition-opacity duration-500 group-hover:opacity-60"
                        />
                      )}
                    </div>
                    <figcaption
                      className={
                        isLifestyle
                          ? "border-t border-white/5 bg-[#0a0a0c] px-6 py-5"
                          : "border-t border-black/5 bg-white/60 px-6 py-5 backdrop-blur"
                      }
                    >
                      <span
                        className={
                          isLifestyle
                            ? "font-tech text-[10px] uppercase tracking-[0.32em] text-[#c6a87c]"
                            : "font-tech text-[10px] uppercase tracking-[0.32em] text-[#8a6a3d]"
                        }
                      >
                        {block.labelPrefix} · 0{i + 1}
                      </span>
                      <h3
                        className={
                          isLifestyle
                            ? "mt-2 font-serif-display text-lg font-bold uppercase tracking-tight text-white"
                            : "mt-2 font-serif-display text-lg font-bold uppercase tracking-tight text-[#0a0a0c]"
                        }
                      >
                        {g.name}
                      </h3>
                      <p
                        className={
                          isLifestyle
                            ? "mt-2 font-body text-[13px] leading-relaxed text-white/65"
                            : "mt-2 font-body text-[13px] leading-relaxed text-[#0a0a0c]/65"
                        }
                      >
                        {g.spec}
                      </p>
                    </figcaption>
                  </motion.figure>
                );
              })}
            </div>
          </div>
        ))}

        {/* Related systems — internal linking strip */}
        {relatedCats.length > 0 && (
          <section
            data-testid="related-systems"
            className="mt-24 border-t border-white/10 pt-16"
          >
            <div className="mb-10 text-center">
              <span className="font-tech text-[10px] uppercase tracking-[0.5em] text-[#c6a87c]">
                — Related Systems
              </span>
              <h2 className="mt-4 font-serif-display text-3xl font-bold uppercase tracking-tight text-white lg:text-4xl">
                Explore the Range
              </h2>
            </div>
            <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-5">
              {relatedCats.map((rc) => (
                <Link
                  key={rc.slug}
                  to={`/products/${rc.slug}`}
                  data-testid={`related-system-${rc.slug}`}
                  className="group relative overflow-hidden rounded-sm border border-white/10 bg-[#0f0f11] transition-all duration-500 hover:border-[#c6a87c] hover:shadow-[0_0_50px_-8px_rgba(198,168,124,0.35)]"
                >
                  <div className="relative aspect-[4/5] overflow-hidden bg-black">
                    <img
                      src={rc.heroImage}
                      alt={`${rc.title.replace(/\.$/, "")} — EUROSTEEL`}
                      loading="lazy"
                      className="absolute inset-0 h-full w-full object-cover opacity-70 transition-all duration-[900ms] ease-[cubic-bezier(0.19,1,0.22,1)] group-hover:scale-[1.06] group-hover:opacity-100"
                    />
                    <div
                      aria-hidden
                      className="pointer-events-none absolute inset-0 bg-gradient-to-t from-black/85 via-black/30 to-transparent"
                    />
                    <div className="absolute inset-x-0 bottom-0 p-4">
                      <p className="font-tech text-[9px] uppercase tracking-[0.32em] text-[#c6a87c]">
                        {rc.eyebrow || "System"}
                      </p>
                      <p className="mt-1 font-serif-display text-[15px] font-bold uppercase leading-tight tracking-tight text-white lg:text-base">
                        {rc.title.replace(/\.$/, "")}
                      </p>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          </section>
        )}

        {/* CTAs — bronze glow ring */}
        <div className="mt-20 flex flex-wrap justify-center gap-4">
          <Link
            to="/contact"
            data-testid="product-category-enquire"
            className="group relative rounded-full bg-[#c6a87c] px-12 py-4 font-tech text-[11px] uppercase tracking-[0.35em] text-[#0a0a0c] shadow-[0_0_0_0_rgba(198,168,124,0.6)] transition-all duration-500 hover:bg-[#d8bd93] hover:shadow-[0_0_40px_6px_rgba(198,168,124,0.35)]"
          >
            Enquire
          </Link>
          <Link
            to="/products"
            className="rounded-full border border-white/25 px-12 py-4 font-tech text-[11px] uppercase tracking-[0.35em] text-white/80 transition-all duration-500 hover:border-[#c6a87c] hover:bg-[#c6a87c]/10 hover:text-[#c6a87c]"
          >
            All Products
          </Link>
        </div>
      </section>
    </div>
  );
}
